import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortable } from "@dnd-kit/react/sortable";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";
import { SingleSelectArchivedDivider } from "@/pages/applications/pages/customFields/components/SingleSelectArchivedDivider";
import { SingleSelectOptionRow } from "@/pages/applications/pages/customFields/components/SingleSelectOptionRow";
import { CUSTOM_FIELD_OPTION_LIMIT_PER_FIELD } from "@/pages/applications/pages/customFields/constants";
import type {
  OptionSection,
  SingleSelectOptionsDraft,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";
import {
  addOption,
  countOptions,
  fillOptionNamesFrom,
  getOptionDescriptionError,
  getOptionNameErrors,
  moveOptionEntry,
  removeOption,
  setOptionSection,
  toOptionEntries,
  updateOption,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  counter: {
    color: theme.colors.staticFgNote,
  },
  counterAtLimit: {
    color: theme.colors.staticFgAttentive,
  },
  list: {
    gap: "8px",
  },
  empty: {
    padding: "16px",
    border: `1px dashed ${theme.colors.neutral030}`,
    borderRadius: "4px",
    color: theme.colors.staticFgNote,
    textAlign: "center",
  },
  addButtonRow: {
    display: "flex",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface SingleSelectOptionEditorProps {
  value: SingleSelectOptionsDraft;
  onChange: (next: SingleSelectOptionsDraft) => void;
  /**
   * How many options this field may ever hold, counting archived ones. Defaults
   * to the product limit; a story or a test passes a small number to reach the
   * full state without building two hundred rows.
   */
  maxItems?: number;
  disabled?: boolean;
  sx?: SxProps<typeof muiTheme>;
}

/**
 * The option list for a single select custom field: add, rename, describe,
 * archive, restore, reorder.
 *
 * Controlled and presentational -- it takes the draft and hands back the next
 * one, reads no query and calls no mutation, so a story drives it exactly the
 * way the create/edit modal does. Validation is derived from `value` on every
 * render rather than stored, so there is no second copy of the truth to keep in
 * sync.
 *
 * Rows and the archived divider share one sortable list, which is what lets the
 * divider mean something: a drag reports one index into that list, and the row's
 * section is simply which side of the divider entry it landed on. There is no
 * boundary case to guess at, and dragging across the divider is what archives or
 * restores a row.
 *
 * Two things it does keep to itself, because neither is part of what gets
 * saved: which rows have their description open, and which rows the user has
 * left once. The second exists so a freshly added row does not greet the user
 * with "Option name is required" before they have typed anything -- that one
 * error waits for the row to be touched, while every other error shows as soon
 * as it is true.
 */
export const SingleSelectOptionEditor: React.FC<SingleSelectOptionEditorProps> =
  function SingleSelectOptionEditor({
    value,
    onChange,
    maxItems = CUSTOM_FIELD_OPTION_LIMIT_PER_FIELD,
    disabled = false,
    sx,
  }) {
    const { t } = useTranslation();
    const [expandedRowIds, setExpandedRowIds] = useState<ReadonlySet<string>>(
      () => new Set(),
    );
    const [touchedRowIds, setTouchedRowIds] = useState<ReadonlySet<string>>(
      () => new Set(),
    );
    // How many pasted lines the option limit left no room for. Kept only long
    // enough to tell the user, and cleared by the next paste.
    const [pasteDroppedCount, setPasteDroppedCount] = useState(0);

    const nameErrors = useMemo(() => getOptionNameErrors(value), [value]);
    // One flat list holding rows and the divider together, so a drag reports a
    // single index and the divider is a drop target like any other entry.
    const entries = useMemo(() => toOptionEntries(value), [value]);
    const dividerIndex = entries.findIndex((entry) => entry.kind === "divider");
    const total = countOptions(value);
    const isAtLimit = total >= maxItems;

    const handleNameChange = useHandler((rowId: string, name: string) => {
      onChange(updateOption(value, rowId, { name }));
    });

    const handleNameBlur = useHandler((rowId: string) => {
      setTouchedRowIds((current) =>
        current.has(rowId) ? current : new Set(current).add(rowId),
      );
    });

    const handleNamesPaste = useHandler(
      (rowId: string, names: ReadonlyArray<string>) => {
        const filled = fillOptionNamesFrom(value, rowId, names, maxItems);
        setPasteDroppedCount(filled.droppedCount);
        onChange(filled.draft);
      },
    );

    const handleDescriptionChange = useHandler(
      (rowId: string, description: string) => {
        onChange(updateOption(value, rowId, { description }));
      },
    );

    const handleExpandedChange = useHandler(
      (rowId: string, expanded: boolean) => {
        setExpandedRowIds((current) => {
          const next = new Set(current);
          if (expanded) next.add(rowId);
          else next.delete(rowId);
          return next;
        });
      },
    );

    const handleSectionChange = useHandler(
      (rowId: string, section: OptionSection) => {
        onChange(setOptionSection(value, rowId, section));
      },
    );

    const handleRemove = useHandler((rowId: string) => {
      onChange(removeOption(value, rowId));
    });

    const handleAdd = useHandler(() => {
      onChange(addOption(value));
    });

    const handleDragEnd = useHandler<
      React.ComponentProps<typeof DragDropProvider>["onDragEnd"]
    >((event) => {
      if (event.canceled) return;
      const { source } = event.operation;
      if (!isSortable(source)) return;
      const { initialIndex, index } = source;
      if (initialIndex === index) return;
      onChange(moveOptionEntry(value, initialIndex, index));
    });

    return (
      <Box sx={{ ...styles.root, ...sx }}>
        <Box sx={styles.header}>
          <Box sx={sharedStyles.label}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("customField.singleSelect.label")}
            </Typography>
            <Box component="span" sx={sharedStyles.required}>
              *
            </Box>
          </Box>
          <Box
            component="span"
            sx={{ ...styles.counter, ...(isAtLimit && styles.counterAtLimit) }}
          >
            <Typography variant="note" color="inherit">
              {total}/{maxItems}
            </Typography>
          </Box>
        </Box>

        {total === 0 ? (
          <Box sx={styles.empty}>
            <Typography variant="note" color="inherit">
              {t("customField.singleSelect.empty")}
            </Typography>
          </Box>
        ) : (
          <DragDropProvider onDragEnd={handleDragEnd}>
            <Stack sx={styles.list}>
              {entries.map((entry, index) =>
                entry.kind === "divider" ? (
                  <SingleSelectArchivedDivider
                    key="archived-divider"
                    index={index}
                  />
                ) : (
                  <SingleSelectOptionRow
                    key={entry.option.rowId}
                    option={entry.option}
                    section={
                      dividerIndex !== -1 && index > dividerIndex
                        ? "archived"
                        : "active"
                    }
                    index={index}
                    // An untouched row is one the user just added; holding back
                    // only the "required" error keeps the list from turning red
                    // the moment a row appears.
                    nameError={
                      nameErrors.get(entry.option.rowId) === "empty" &&
                      !touchedRowIds.has(entry.option.rowId)
                        ? null
                        : (nameErrors.get(entry.option.rowId) ?? null)
                    }
                    descriptionError={getOptionDescriptionError(
                      entry.option.description,
                    )}
                    expanded={expandedRowIds.has(entry.option.rowId)}
                    disabled={disabled}
                    onNameChange={handleNameChange}
                    onNamesPaste={handleNamesPaste}
                    onNameBlur={handleNameBlur}
                    onDescriptionChange={handleDescriptionChange}
                    onExpandedChange={handleExpandedChange}
                    onSectionChange={handleSectionChange}
                    onRemove={handleRemove}
                  />
                ),
              )}
            </Stack>
          </DragDropProvider>
        )}

        <Box sx={styles.addButtonRow}>
          <Button
            variant="plain"
            startIcon={<MotifIcon un-i-motif="plus" />}
            onClick={handleAdd}
            disabled={disabled || isAtLimit}
          >
            {t("customField.singleSelect.addOption")}
          </Button>
        </Box>

        {isAtLimit ? (
          <Typography variant="note" color={theme.colors.staticFgAttentive}>
            {t("customField.singleSelect.limitReached")}
          </Typography>
        ) : null}

        {pasteDroppedCount > 0 ? (
          <Typography variant="note" color={theme.colors.staticFgNegative}>
            {t("customField.singleSelect.pasteDropped", {
              count: pasteDroppedCount,
              limit: maxItems,
            })}
          </Typography>
        ) : null}
      </Box>
    );
  };
